import { useState } from "react";
import { ShieldCheckIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { regimeArtwork } from "./regime-artwork";
import { artworkCropStyles, ISSUER_SYMBOL_CROPS, type ArtworkCrop } from "./regimeArtworkCrop";

function IssuerMark({
  src,
  label,
  prominent,
  compact,
  dark,
  crop,
}: {
  src: string;
  label: string;
  prominent: boolean;
  compact: boolean;
  dark: boolean;
  crop: ArtworkCrop | undefined;
}) {
  const [failed, setFailed] = useState(false);
  const styles = crop ? artworkCropStyles(crop) : null;
  return (
    <Avatar
      title={label}
      className={
        prominent
          ? "size-9 rounded-none bg-transparent"
          : compact
            ? "size-3.5 rounded-none bg-transparent"
            : "size-8 rounded-none bg-transparent"
      }
    >
      {failed ? (
        <AvatarFallback className="rounded-none bg-transparent">
          <ShieldCheckIcon className="size-4 text-muted-foreground" />
        </AvatarFallback>
      ) : (
        <span className="relative block size-full overflow-hidden" style={styles?.frame}>
          <AvatarImage
            style={styles?.image}
            src={src}
            alt=""
            width={80}
            height={56}
            loading={prominent ? "eager" : "lazy"}
            decoding="async"
            onError={() => setFailed(true)}
            className={`rounded-none object-contain object-left ${dark ? "invert dark:invert-0" : "mix-blend-multiply dark:mix-blend-normal"}`}
          />
        </span>
      )}
    </Avatar>
  );
}

/** Issuer artwork complements the adjacent issuer text; it is not a certification badge. */
export function RegimeArtwork({
  regimeId,
  prominent = false,
  compact = false,
}: {
  regimeId: string;
  prominent?: boolean;
  /** One 14px mark for a label pill: the first issuer only, no fallback shield. */
  compact?: boolean;
}) {
  const all = regimeArtwork(regimeId);
  const marks = compact ? all.slice(0, 1) : all;
  if (compact && marks.length === 0) return null;
  return (
    <span
      data-regime-artwork={regimeId}
      aria-hidden="true"
      className={
        marks.length > 1
          ? "grid shrink-0 grid-cols-2 items-center gap-1 [&>span]:h-6 [&>span]:w-6"
          : "flex shrink-0 items-center"
      }
    >
      {marks.length ? (
        marks.map((mark) => (
          <IssuerMark
            key={mark.src}
            src={mark.src}
            label={mark.label}
            prominent={prominent}
            compact={compact}
            dark={mark.dark}
            crop={ISSUER_SYMBOL_CROPS[mark.key]}
          />
        ))
      ) : (
        <Avatar className="size-8 rounded-none bg-transparent">
          <AvatarFallback className="rounded-none">
            <ShieldCheckIcon className="size-4 text-muted-foreground" />
          </AvatarFallback>
        </Avatar>
      )}
    </span>
  );
}
